import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import InsightsPanel from '@/components/procurement/InsightsPanel';
import InlineEdit from '@/components/procurement/InlineEdit';
import UniversalFilterBar from '@/components/procurement/UniversalFilterBar';
import {
  Loader2, Sparkles, Clock, AlertOctagon, AlertTriangle, CheckCircle2,
  DollarSign, Send, X, LinkIcon, Filter, FileSpreadsheet, Siren,
  ArrowUp, ArrowDown, Minus, ChevronsUp, ChevronsDown,
} from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};

const STATUS = {
  ON_TIME:  { color: '#22C55E', label: 'On-Time' },
  AT_RISK:  { color: '#F59E0B', label: 'At-Risk' },
  DELAYED:  { color: '#EF4444', label: 'Delayed' },
  CRITICAL: { color: 'var(--tone-red-text)', label: 'Critical' },
  UNKNOWN:  { color: 'var(--text-muted)', label: 'Unknown' },
};

const Delta = ({ v, invert }) => {
  if (v == null || v === 0) return <span className="inline-flex items-center text-[9px]" style={{ color: C.slateMuted }}><Minus className="w-2 h-2" />0</span>;
  const isBad = invert ? v < 0 : v > 0;
  const Icon = v > 0 ? ArrowUp : ArrowDown;
  return <span className="inline-flex items-center gap-0.5 text-[9px] font-mono" style={{ color: isBad ? '#EF4444' : '#22C55E' }}><Icon className="w-2 h-2" />{Math.abs(v)}</span>;
};

// The brand navy is too dark to read on the obsidian theme — labels use the
// theme-lifted tone while the card border keeps the hex (var() can't take an alpha suffix).
const TONE_TEXT = { '#1E4E8C': 'var(--tone-navy-text)' };

const KPI = ({ label, value, tone, icon: Icon, sub, delta, deltaInvert }) => (
  <div className="p-3 rounded-lg" style={{ background: C.surface, border: `1px solid ${tone}33` }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1 text-[10px] uppercase font-semibold tracking-[0.08em]" style={{ color: TONE_TEXT[tone] || tone }}>
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {delta !== undefined && <Delta v={delta} invert={deltaInvert} />}
    </div>
    <div className="mt-1 text-2xl font-bold leading-none" style={{ color: C.slate }}>{value}</div>
    {sub && <div className="mt-0.5 text-[10px]" style={{ color: C.slateMuted }}>{sub}</div>}
  </div>
);

function TimelineBar({ row, minDate, maxDate }) {
  const totalDays = Math.max(1, (maxDate - minDate) / 86400000);
  const fdd = row.fdd ? new Date(row.fdd) : null;
  const ras = row.ras ? new Date(row.ras) : null;
  if (!fdd || !ras) return <div className="text-[10px]" style={{ color: C.slateMuted }}>Insufficient dates</div>;
  const fddPct = Math.max(0, Math.min(100, ((fdd - minDate) / 86400000 / totalDays) * 100));
  const rasPct = Math.max(0, Math.min(100, ((ras - minDate) / 86400000 / totalDays) * 100));
  const status = STATUS[row.status] || STATUS.UNKNOWN;
  const left = Math.min(fddPct, rasPct);
  const width = Math.max(1, Math.abs(rasPct - fddPct));
  return (
    <div className="relative h-6" style={{ background: 'var(--card-bg-alt)', borderRadius: 4 }}>
      <div className="absolute top-1 h-4 rounded-sm" style={{ left: `${left}%`, width: `${width}%`, background: `${status.color}55`, border: `1px solid ${status.color}` }} />
      <div className="absolute top-0 h-6 w-[2px]" style={{ left: `${fddPct}%`, background: C.primaryText }} title={`FDD: ${fdd.toLocaleDateString()}`} />
      <div className="absolute top-0 h-6 w-[2px]" style={{ left: `${rasPct}%`, background: C.accent }} title={`RAS: ${ras.toLocaleDateString()}`} />
    </div>
  );
}

function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center" onClick={onCancel} data-testid="confirm-dialog">
      <div className="w-full max-w-md p-5 rounded-lg" onClick={e => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <div className="text-sm font-semibold" style={{ color: C.slate }}>{title}</div>
        <div className="text-xs mt-2" style={{ color: C.slateMuted }}>{message}</div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={onConfirm} className="text-white" style={{ background: C.accent }} data-testid="confirm-btn">Confirm</Button>
        </div>
      </div>
    </div>
  );
}

function DisciplineBreakdown({ byDiscipline }) {
  const entries = Object.entries(byDiscipline || {}).slice(0, 8);
  if (!entries.length) return null;
  return (
    <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="df-breakdown">
      <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2" style={{ color: C.primaryText }}>Breakdown by Discipline</div>
      <div className="space-y-1.5">
        {entries.map(([name, s]) => {
          const total = Object.values(s).reduce((a, b) => a + b, 0);
          return (
            <div key={name}>
              <div className="flex justify-between text-[10px]" style={{ color: C.slateMuted }}>
                <span>{name}</span><span className="font-mono">{total}</span>
              </div>
              <div className="flex h-2 rounded overflow-hidden">
                {['ON_TIME', 'AT_RISK', 'DELAYED', 'CRITICAL', 'UNKNOWN'].map(st => {
                  const pct = total ? (s[st] / total) * 100 : 0;
                  if (pct === 0) return null;
                  return <div key={st} style={{ width: `${pct}%`, background: STATUS[st].color }} title={`${st}: ${s[st]}`} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DeliveryForecast({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [filters, setFilters] = useState({ discipline: '', vendor: '', priority: '', search: '', materialCategory: 'ALL' });
  const [sort, setSort] = useState({ by: 'buffer', dir: 'asc' });
  const [selected, setSelected] = useState({});
  const [writing, setWriting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [autoRaising, setAutoRaising] = useState(false);
  const [rollups, setRollups] = useState([]);
  const [openMci, setOpenMci] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;
  const debounceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'ALL') params.append(k, v); });
      params.append('status', statusFilter); params.append('sortBy', sort.by); params.append('sortDir', sort.dir);
      params.append('limit', PAGE_SIZE); params.append('offset', page * PAGE_SIZE);
      const { data: d } = await api.get(`/v1/projects/${projectId}/mci/control-tower/delivery-forecast?${params}`);
      setData(d);
    } catch (e) { toast.error('Failed to load Delivery Forecast'); }
    setLoading(false);
  };

  const doWriteBack = async () => {
    setConfirming(false);
    const codes = Object.keys(selected).filter(k => selected[k]);
    if (codes.length === 0) return toast.warning('Select at least one row');
    setWriting(true);
    try {
      const { data: r } = await api.post(`/v1/projects/${projectId}/mci/control-tower/delivery-forecast/write-back`,
        { mciLineNumbers: codes, milestoneLabel: 'Material Available at Site', createMilestone: true, linkAsDependency: true });
      toast.success(`${r.writtenBack} rolled up · ${r.milestonesCreated} milestones · ${r.dependenciesAdded} deps wired · CPM re-run required`, {
        action: r.deepLink ? { label: 'Open Schedule', onClick: () => window.open(r.deepLink, '_blank') } : undefined
      });
      setSelected({}); loadRollups();
    } catch (e) { toast.error('Write-back failed'); }
    setWriting(false);
  };

  const autoRaiseCriticalRaid = async () => {
    setAutoRaising(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/actions/auto-raid-critical`, { sendEmailDigest: true });
      toast.success(`${r.data.raised} CRITICAL slippages raised to RAID`, {
        action: r.data.deepLink ? { label: 'Open RAID Log', onClick: () => window.open(r.data.deepLink, '_blank') } : undefined
      });
    } catch (e) { toast.error('Auto-raise failed'); }
    setAutoRaising(false);
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/v1/projects/${projectId}/mci/control-tower/export/xlsx?phase=DELIVERY`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'delivery-forecast.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const loadRollups = async () => {
    try {
      const { data: r } = await api.get(`/v1/projects/${projectId}/mci/control-tower/delivery-forecast/write-back/latest`);
      setRollups(r.rollups || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [projectId, filters, statusFilter, sort, page]);

  useEffect(() => {
    loadRollups();
    const h = () => load();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
    // eslint-disable-next-line
  }, [projectId]);

  const { minDate, maxDate } = useMemo(() => {
    if (!data?.rows?.length) {
      const now = new Date(); return { minDate: now, maxDate: new Date(now.getTime() + 90 * 86400000) };
    }
    let min = Infinity, max = -Infinity;
    data.rows.forEach(r => {
      [r.fdd, r.ras].forEach(d => { if (!d) return; const t = new Date(d).getTime(); if (t < min) min = t; if (t > max) max = t; });
    });
    if (min === Infinity) { const now = new Date(); return { minDate: now, maxDate: new Date(now.getTime() + 90 * 86400000) }; }
    return { minDate: new Date(min - 7 * 86400000), maxDate: new Date(max + 7 * 86400000) };
  }, [data]);

  if (loading && !data) return (
    <div className="p-12 text-center text-sm" style={{ color: C.slateMuted }}>
      <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" style={{ color: C.primaryText }} />
      Loading Delivery Forecast…
    </div>
  );
  if (!data) return null;

  const k = data.kpis; const dd = data.deltas || {};
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleSort = (by) => setSort(s => ({ by, dir: s.by === by && s.dir === 'asc' ? 'desc' : 'asc' }));
  const SortHead = ({ col, label, align = 'left' }) => (
    <th className={`p-1.5 text-${align} cursor-pointer select-none`} onClick={() => toggleSort(col)} style={{ color: C.slateMuted }} data-testid={`df-sort-${col}`}>
      <span className="inline-flex items-center gap-0.5">{label}
        {sort.by === col && (sort.dir === 'asc' ? <ChevronsUp className="w-3 h-3" /> : <ChevronsDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="delivery-forecast" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="col-span-12 lg:col-span-9 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <KPI label="Total"    value={k.total}    tone={C.primary} icon={Clock}          delta={dd.total} />
          <KPI label="On-Time"  value={k.onTime}   tone="#22C55E"   icon={CheckCircle2}   delta={dd.onTime} />
          <KPI label="At-Risk"  value={k.atRisk}   tone="#F59E0B"   icon={AlertTriangle}  delta={dd.atRisk}   deltaInvert />
          <KPI label="Delayed"  value={k.delayed}  tone="#EF4444"   icon={AlertOctagon}   delta={dd.delayed}  deltaInvert />
          <KPI label="Critical" value={k.critical} tone="#B91C1C"   icon={AlertOctagon}   delta={dd.critical} deltaInvert />
          <KPI label="Exposure" value={`$${(k.exposureValue / 1000).toFixed(0)}k`} tone={C.accent} icon={DollarSign} sub={`Avg buffer ${k.avgBufferDays}d`} delta={dd.exposureValue} deltaInvert />
        </div>

        {/* RAS source badges + WBS-link coverage banner */}
        {data.rasSources && (
          <div className="flex items-center gap-3 flex-wrap text-[10px]" data-testid="df-ras-sources">
            <span style={{ color: C.slateMuted }}>RAS Source:</span>
            <span className="px-2 py-0.5 rounded" style={{ background: `${C.primary}18`, color: C.primaryText }}>
              WBS-linked: <b>{data.rasSources.wbs || 0}</b>
            </span>
            <span className="px-2 py-0.5 rounded" style={{ background: `${C.accent}18`, color: C.accent }}>
              Manual: <b>{data.rasSources.manual || 0}</b>
            </span>
            <span className="px-2 py-0.5 rounded" style={{ background: '#94A3B822', color: C.slateMuted }}>
              Estimated (MR + lead time): <b>{data.rasSources.estimated || 0}</b>
            </span>
          </div>
        )}
        {data.rasSources && (data.rasSources.manual + data.rasSources.estimated) > 0 && (
          <div className="rounded-lg p-2 flex items-center gap-2 text-[11px]"
            style={{ background: `${C.accent}12`, border: `1px dashed ${C.accent}` }} data-testid="df-wbs-banner">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: C.accent }} />
            <div style={{ color: C.slate }}>
              <b style={{ color: C.accent }}>{data.rasSources.manual + data.rasSources.estimated} of {data.kpis.total}</b> packages
              are missing a linked WBS activity — RAS falls back to manual or estimated. Link them from the MCI Register
              (or run auto-backfill) to unlock schedule-driven Required-at-Site dates.
            </div>
          </div>
        )}

        {/* Universal Filter Bar */}
        <UniversalFilterBar
          search={filters.search} onSearchChange={v => setFilters({ ...filters, search: v })}
          searchPlaceholder="Search MCI / desc / vendor…"
          category={filters.materialCategory} onCategoryChange={v => setFilters({ ...filters, materialCategory: v })}
          categoryOptions={[{ value:'ALL', label:'ALL' }, { value:'BULK', label:'BULK' }, { value:'TAGGED', label:'TAGGED' }]}
          priority={filters.priority || 'ALL'} onPriorityChange={v => setFilters({ ...filters, priority: v === 'ALL' ? '' : v })}
          priorityOptions={[
            { value:'ALL', label:'ALL' }, { value:'CRITICAL', label:'CRIT' },
            { value:'HIGH', label:'HIGH' }, { value:'NORMAL', label:'NORMAL' },
            { value:'LOW', label:'LOW' },
          ]}
          phase={statusFilter} onPhaseChange={setStatusFilter}
          phaseOptions={['ALL', 'CRITICAL', 'DELAYED', 'AT_RISK', 'ON_TIME', 'UNKNOWN'].map(f => ({
            value: f, label: f === 'ALL' ? 'All' : (STATUS[f]?.label || f),
          }))}
          vendor={filters.vendor} onVendorChange={v => setFilters({ ...filters, vendor: v })}
        />

        {/* Trailing actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px]" style={{ color: C.slateMuted }}>{selectedCount} selected · {data.rows.length} rows</span>
            <PermissionGate module="MCI" action="export">
              <Button size="sm" variant="outline" onClick={exportXlsx} className="h-7 text-[10px]" data-testid="df-export-btn">
                <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
              </Button>
            </PermissionGate>
            <PermissionGate module="RAID" action="create">
              <Button size="sm" onClick={autoRaiseCriticalRaid} disabled={autoRaising || k.critical === 0}
                className="h-7 text-[10px] text-white" style={{ background: 'var(--tone-red-text)' }} data-testid="df-auto-raid-btn">
                {autoRaising ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Siren className="w-3 h-3 mr-1" />}
                Auto-raise {k.critical} to RAID
              </Button>
            </PermissionGate>
            <PermissionGate module="MCI" action="update">
              <Button size="sm" onClick={() => setConfirming(true)} disabled={writing || selectedCount === 0}
                className="h-7 text-[10px] text-white" style={{ background: C.accent }} data-testid="df-writeback-btn">
                {writing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Roll-up to Macro Schedule
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* FDD vs RAS timeline table with sortable headers */}
        <div className="rounded-lg" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="df-timeline-table">
          <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.primaryText }}>Forecast Delivery vs Required-At-Site</div>
            <div className="flex items-center gap-3 text-[10px]" style={{ color: C.slateMuted }}>
              <span className="flex items-center gap-1"><span className="w-2 h-3" style={{ background: C.primaryText }} /> FDD</span>
              <span className="flex items-center gap-1"><span className="w-2 h-3" style={{ background: C.accent }} /> RAS</span>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0" style={{ background: C.surface }}>
                <tr>
                  <th className="p-1.5 w-8">
                    <input type="checkbox"
                      checked={selectedCount > 0 && selectedCount === data.rows.length}
                      onChange={e => {
                        const on = e.target.checked;
                        const sel = {};
                        if (on) data.rows.slice(0, 200).forEach(r => sel[r.mciLineNumber] = true);
                        setSelected(sel);
                      }} data-testid="df-select-all" />
                  </th>
                  <SortHead col="mciLineNumber" label="Package" />
                  <SortHead col="vendor" label="Vendor" />
                  <th className="p-1.5 text-left" style={{ color: C.slateMuted, minWidth: 240 }}>Timeline</th>
                  <SortHead col="fdd" label="FDD" align="right" />
                  <SortHead col="ras" label="RAS" align="right" />
                  <SortHead col="buffer" label="Buffer" align="right" />
                  <SortHead col="status" label="Status" align="center" />
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center" style={{ color: C.slateMuted }}>No packages for filter.</td></tr>
                )}
                {data.rows.slice(0, 200).map(r => {
                  const s = STATUS[r.status];
                  return (
                    <tr key={r.mciLineNumber} className="hover:bg-blue-50/40 cursor-pointer" style={{ borderTop: `1px solid ${C.border}` }} data-testid={`df-row-${r.mciLineNumber}`}
                      onClick={(e) => { if (e.target.tagName !== 'INPUT') setOpenMci(r.mciLineNumber); }}
                      title={`Click to open drilldown for ${r.mciLineNumber}`}>
                      <td className="p-1.5 text-center">
                        <input type="checkbox" checked={!!selected[r.mciLineNumber]}
                          onChange={e => setSelected({ ...selected, [r.mciLineNumber]: e.target.checked })}
                          data-testid={`df-select-${r.mciLineNumber}`} />
                      </td>
                      <td className="p-1.5">
                        <div className="font-mono font-bold text-[10px]" style={{ color: C.primaryText }}>{r.mciLineNumber}</div>
                        <div className="truncate max-w-[180px]" style={{ color: C.slate }}>{r.materialDescription}</div>
                      </td>
                      <td className="p-1.5" onClick={e => e.stopPropagation()}>
                        <InlineEdit value={r.vendor} projectId={projectId} mci={r.mciLineNumber}
                          field="vendor" onSaved={load} testIdPrefix="df-vendor"
                          style={{ color: C.slateMuted }} />
                      </td>
                      <td className="p-1.5"><TimelineBar row={r} minDate={minDate} maxDate={maxDate} /></td>
                      <td className="p-1.5 text-right font-mono" style={{ color: C.primaryText }}>{r.fdd?.slice(0, 10) || '—'}</td>
                      <td className="p-1.5 text-right font-mono" title={`Source: ${r.rasSource}`} style={{ color: C.accent }}>
                        {r.ras?.slice(0, 10) || '—'}
                        {r.rasSource === 'wbs' && <sup className="ml-0.5 text-[8px]" style={{ color: '#22C55E' }}>WBS</sup>}
                      </td>
                      <td className="p-1.5 text-right font-mono font-bold" style={{ color: s.color }}>{r.bufferDays !== null ? `${r.bufferDays}d` : '—'}</td>
                      <td className="p-1.5 text-center">
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: s.color }}>{s.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination footer */}
          {data.pagination && (
            <div className="flex items-center justify-between p-2 border-t text-[10px]" style={{ borderColor: C.border, color: C.slateMuted }} data-testid="df-pagination">
              <div>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.totalRows)} of {data.totalRows}</div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="h-6 text-[10px]" data-testid="df-prev">Prev</Button>
                <span>Page {page + 1}</span>
                <Button size="sm" variant="ghost" disabled={!data.pagination.hasMore} onClick={() => setPage(p => p + 1)} className="h-6 text-[10px]" data-testid="df-next">Next</Button>
              </div>
            </div>
          )}
        </div>

        {rollups.length > 0 && (
          <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="df-rollups">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1" style={{ color: C.primaryText }}>
                <LinkIcon className="w-3 h-3" /> Macro Schedule Roll-ups · {rollups.length}
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: `${C.accent}22`, color: C.accent }}>CPM re-run required</span>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {rollups.slice(0, 25).map((r, i) => (
                <div key={i} className="flex items-center justify-between text-[10px] py-1" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono font-bold" style={{ color: C.primaryText }}>{r.mciLineNumber}</span>
                    <span className="truncate" style={{ color: C.slateMuted }}>{r.milestoneLabel}</span>
                  </div>
                  <div className="flex items-center gap-3 font-mono" style={{ color: C.slateMuted }}>
                    <span style={{ color: C.primaryText }}>FDD {r.fdd?.slice(0, 10) || '—'}</span>
                    <span style={{ color: C.accent }}>RAS {r.ras?.slice(0, 10) || '—'}</span>
                    <span className="px-1 rounded text-white" style={{ background: STATUS[r.status]?.color || 'var(--text-muted)' }}>{r.bufferDays}d</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cognara sidebar + breakdown */}
      <div className="col-span-12 lg:col-span-3 space-y-3">
        <DisciplineBreakdown byDiscipline={data.byDiscipline} />
        {data.byArea && Object.keys(data.byArea).length > 0 && (
          <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="df-byarea">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2" style={{ color: C.primaryText }}>Breakdown by Area</div>
            <div className="space-y-1.5">
              {Object.entries(data.byArea).slice(0, 8).map(([name, s]) => {
                const total = Object.values(s).reduce((a, b) => a + b, 0);
                return (
                  <div key={name}>
                    <div className="flex justify-between text-[10px]" style={{ color: C.slateMuted }}>
                      <span className="truncate max-w-[140px]">{name}</span><span className="font-mono">{total}</span>
                    </div>
                    <div className="flex h-2 rounded overflow-hidden">
                      {['ON_TIME', 'AT_RISK', 'DELAYED', 'CRITICAL', 'UNKNOWN'].map(st => {
                        const pct = total ? (s[st] / total) * 100 : 0;
                        if (pct === 0) return null;
                        return <div key={st} style={{ width: `${pct}%`, background: STATUS[st].color }} title={`${st}: ${s[st]}`} />;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <InsightsPanel projectId={projectId} phase="DELIVERY_FORECAST" color={C.primary} testId="df-insights" />
      </div>

      {confirming && (
        <ConfirmDialog
          title="Roll up FDD to Macro Schedule?"
          message={`This will (1) upsert a milestone task per selected MCI on the macro schedule, (2) mark cpmRerunRequired=true. Planners must re-run CPM manually. Selected: ${selectedCount} package(s).`}
          onConfirm={doWriteBack} onCancel={() => setConfirming(false)} />
      )}
      {openMci && <MciPackageDrawer projectId={projectId} mci={openMci} onClose={() => { setOpenMci(null); load(); }} />}
    </div>
  );
}
